import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Truck, Phone, Mail, Building2, Search, MessageCircle, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DispatchSupplierDialog from '../components/DispatchSupplierDialog';
import SupplierPaymentDialog from '../components/SupplierPaymentDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  company_name: '',
  contact_person: '',
  phone: '',
  email: '',
  address: '',
  uen: '',
  service_type: '',
  payment_method: '',
  bank_name: '',
  bank_account_no: '',
  paynow_uen: '',
  payment_terms: '',
  status: 'active',
  notes: '',
};

const SERVICE_TYPES = [
  'Lorry Crane', 'Tailgate Lorry', 'Trailer', 'Prime Mover',
  'Manpower', 'Warehouse', 'Equipment Rental', 'Transport', 'Other'
];
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'PayNow', 'Cheque', 'Credit Terms'];

const SupplierManagement = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [search, setSearch] = useState('');

  // Job dispatch via WhatsApp
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobSupplier, setJobSupplier] = useState(null);

  // Supplier payment voucher
  const [showPayModal, setShowPayModal] = useState(false);
  const [paySupplier, setPaySupplier] = useState(null);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/suppliers`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load suppliers'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ ...EMPTY, ...item });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/suppliers/${editingId}`, form);
        toast.success('Supplier updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/suppliers`, form);
        toast.success('Supplier added');
      }
      setShowModal(false);
      fetchSuppliers();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save supplier'));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete supplier "${item.company_name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/suppliers/${item.id}`);
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete'));
    }
  };

  const openJobDispatch = (sup) => {
    if (!sup.phone) {
      toast.error('This supplier has no phone number. Edit supplier to add it.');
      return;
    }
    setJobSupplier(sup);
    setShowJobModal(true);
  };

  const q = search.toLowerCase();
  const filtered = items.filter((s) => {
    if (!q) return true;
    return (
      s.company_name?.toLowerCase().includes(q) ||
      s.contact_person?.toLowerCase().includes(q) ||
      s.phone?.toLowerCase().includes(q) ||
      s.service_type?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/profit-tracker')} data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Building2 size={28} className="text-blue-600" /> Supplier Management
              </h1>
              <p className="text-gray-600 text-sm mt-1">Add, edit and manage your supplier / subcontractor companies</p>
            </div>
          </div>
          <Button onClick={openAdd} data-testid="add-supplier-btn" className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-1" /> Add Supplier
          </Button>
        </div>

        {/* Search */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by company, contact, phone or service type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="supplier-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-gray-500">
              <Building2 size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No suppliers yet</p>
              <p className="text-sm mt-1">Click &quot;Add Supplier&quot; to register your first supplier company.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="supplier-list">
            {filtered.map((s) => (
              <Card key={s.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900 truncate" data-testid={`supplier-name-${s.id}`}>
                          {s.company_name}
                        </h3>
                        {s.status === 'inactive' && (
                          <Badge variant="outline" className="text-gray-500 border-gray-300 text-[10px]">Inactive</Badge>
                        )}
                      </div>
                      {s.service_type && (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[11px] mb-2">
                          <Truck size={11} className="mr-1" /> {s.service_type}
                        </Badge>
                      )}
                      {s.contact_person && (
                        <p className="text-sm text-gray-700">{s.contact_person}</p>
                      )}
                      <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                        {s.phone && <div className="flex items-center gap-1.5"><Phone size={11} /> {s.phone}</div>}
                        {s.email && <div className="flex items-center gap-1.5"><Mail size={11} /> {s.email}</div>}
                      </div>
                      {s.payment_method && (
                        <div className="mt-2 text-[11px] text-gray-500">
                          💳 {s.payment_method}{s.payment_terms ? ` · ${s.payment_terms}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" className="h-8 px-2 bg-rose-500 hover:bg-rose-600 text-white"
                              onClick={() => { setPaySupplier(s); setShowPayModal(true); }}
                              data-testid={`pay-supplier-${s.id}`} title="Record payment to supplier">
                        <DollarSign size={14} className="mr-1" /> Pay
                      </Button>
                      <Button size="sm" className="h-8 px-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                              onClick={() => openJobDispatch(s)} data-testid={`send-job-${s.id}`}
                              title="Send job via WhatsApp">
                        <MessageCircle size={14} className="mr-1" /> Job
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                              onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}
                              title="Edit">
                        <Edit size={14} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => remove(s)} data-testid={`delete-supplier-${s.id}`}
                              title="Delete">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add / Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
              <div className="md:col-span-2">
                <Label>Company Name *</Label>
                <Input value={form.company_name}
                       onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                       placeholder="e.g. ABC Crane Services Pte Ltd"
                       data-testid="form-company-name" />
              </div>

              <div>
                <Label>Contact Person</Label>
                <Input value={form.contact_person}
                       onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                       placeholder="Mr Tan"
                       data-testid="form-contact-person" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone}
                       onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       placeholder="9123 4567"
                       data-testid="form-phone" />
              </div>

              <div>
                <Label>Email</Label>
                <Input value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       placeholder="abc@supplier.com"
                       data-testid="form-email" />
              </div>
              <div>
                <Label>UEN / Reg. No.</Label>
                <Input value={form.uen}
                       onChange={(e) => setForm({ ...form, uen: e.target.value })}
                       placeholder="202301234A"
                       data-testid="form-uen" />
              </div>

              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input value={form.address}
                       onChange={(e) => setForm({ ...form, address: e.target.value })}
                       placeholder="123 Industrial Road, Singapore 123456"
                       data-testid="form-address" />
              </div>

              <div>
                <Label>Service Type</Label>
                <Select value={form.service_type}
                        onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger data-testid="form-service-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status}
                        onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 pt-2 border-t mt-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Payment Details</p>
              </div>

              <div>
                <Label>Preferred Payment Method</Label>
                <Select value={form.payment_method}
                        onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="form-payment-method"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Input value={form.payment_terms}
                       onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                       placeholder="e.g. Cash on Delivery / Net 30"
                       data-testid="form-payment-terms" />
              </div>

              <div>
                <Label>Bank Name</Label>
                <Input value={form.bank_name}
                       onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                       placeholder="DBS / OCBC / UOB"
                       data-testid="form-bank-name" />
              </div>
              <div>
                <Label>Bank Account No.</Label>
                <Input value={form.bank_account_no}
                       onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })}
                       placeholder="123-456789-0"
                       data-testid="form-bank-account" />
              </div>

              <div className="md:col-span-2">
                <Label>PayNow (UEN / Mobile)</Label>
                <Input value={form.paynow_uen}
                       onChange={(e) => setForm({ ...form, paynow_uen: e.target.value })}
                       placeholder="UEN: 202301234A or +65 9123 4567"
                       data-testid="form-paynow" />
              </div>

              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          placeholder="Any extra info: discounts, special arrangements, etc."
                          rows={3}
                          data-testid="form-notes" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)} data-testid="cancel-btn">Cancel</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-supplier-btn">
                {editingId ? 'Save Changes' : 'Add Supplier'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Dispatch via WhatsApp — shared component */}
        <DispatchSupplierDialog
          open={showJobModal}
          onOpenChange={setShowJobModal}
          lockedSupplier={jobSupplier}
          sourceType="manual"
        />

        {/* Supplier Payment Voucher */}
        <SupplierPaymentDialog
          open={showPayModal}
          onOpenChange={setShowPayModal}
          prefilledSupplier={paySupplier}
        />
      </div>
    </div>
  );
};

export default SupplierManagement;
